<template>
  <div class="customer-data-package-setting" v-loading="isLoading">
    <div class="title">下单时客户资料包校验</div>
    <div class="setting-item">
      <div>
        <span>
          <span>设计师/导购必须上传指定资料包后，才能一键下单与传单？</span>
          <el-switch
            v-model="customerDataPackage.status"
            active-text="是"
            inactive-text="否"
            :active-value="0"
            :inactive-value="1"
          >
          </el-switch>
        </span>
        <span class="apply-scope-setting">
          <span>适用范围：</span>
          <el-radio-group v-model="customerDataPackage.applyScope">
            <el-radio :label="1">对全部人校验</el-radio>
            <el-radio :label="2">只对要审的订单进行校验</el-radio>
          </el-radio-group>
        </span>
      </div>
      <div
        class="customer-data-package-table"
        v-if="!customerDataPackage.status"
      >
        <el-table
          :data="customerDataPackage.conditionList"
          border
          style="width: 100%"
          size="mini"
        >
          <el-table-column
            prop="informationPackTypeName"
            label="资料包类型"
            align="center"
            min-width="200"
            show-overflow-tooltip
          />
          <el-table-column align="center" min-width="125">
            <template #header>
              <span>前台是否展示</span>
              <el-tooltip
                content="设置是否对设计师/导购下单时展示该项资料"
                placement="top"
              >
                <i class="el-icon-warning" style="cursor: pointer"></i>
              </el-tooltip>
            </template>
            <template v-slot="{ row }">
              <el-checkbox v-model="row.frontDisplay"></el-checkbox>
            </template>
          </el-table-column>
          <el-table-column align="center" label="必填校验" min-width="125">
            <template v-slot="{ row }">
              <el-checkbox
                v-model="row.requiredVerification"
                @change="handleRequiredVerificationChange(row)"
              ></el-checkbox>
            </template>
          </el-table-column>
          <el-table-column
            align="center"
            label="资料包提交维度"
            min-width="180"
          >
            <template v-slot="{ row }">
              <el-radio-group v-model="row.submissionDimension">
                <el-radio :label="0">按客户</el-radio>
                <el-radio :label="1">按品类</el-radio>
              </el-radio-group>
            </template>
          </el-table-column>
          <el-table-column align="center" min-width="125">
            <template #header>
              <span>关联到订单</span>
              <el-tooltip content="文件上传后关联到订单附件" placement="top">
                <i class="el-icon-warning" style="cursor: pointer"></i>
              </el-tooltip>
            </template>
            <template v-slot="{ row }">
              <el-checkbox v-model="row.relatedOrder"></el-checkbox>
            </template>
          </el-table-column>
          <el-table-column align="center" min-width="380">
            <template #header>
              <span>资料包提交方式</span>
              <el-tooltip
                content="【手工上传附件】需要设计师/导购下单前上传对应文件，【在线办理】目前仅支持客户合同报价在线确认最终报价"
                placement="top"
              >
                <i class="el-icon-warning" style="cursor: pointer"></i>
              </el-tooltip>
            </template>
            <template v-slot="{ row }">
              <div class="submission-methods-row">
                <el-checkbox-group v-model="row.submissionMethodList">
                  <el-checkbox
                    v-for="method in getAvailableSubmissionMethods(row)"
                    :key="method.value"
                    :label="method.value"
                  >
                    {{ method.label }}
                  </el-checkbox>
                </el-checkbox-group>
                <!-- 动态渲染扩展按钮 -->
                <el-button
                  v-for="action in getRowActions(row)"
                  :key="action.key"
                  :type="action.type || 'text'"
                  :size="action.size || 'mini'"
                  @click="handleRowAction(action, row)"
                  :style="action.style || { marginLeft: '8px' }"
                >
                  {{ action.label }}
                </el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column align="center" min-width="200">
            <template #header>
              <span>特例名单</span>
              <el-tooltip
                content="设置在名单中的账号将不校验对应的资料包项是否必传"
                placement="top"
              >
                <i class="el-icon-warning" style="cursor: pointer"></i>
              </el-tooltip>
            </template>
            <template v-slot="{ row }">
              <div class="special-case-list-container">
                <el-tooltip
                  placement="top"
                  :content="getSpecialCaseListText(row.specialCaseList)"
                >
                  <span class="special-case-text">
                    {{ getSpecialCaseListText(row.specialCaseList) }}
                  </span>
                </el-tooltip>
                <el-button
                  :style="{ padding: '0 5px' }"
                  size="medium"
                  type="text"
                  @click="handleSpecialCaseSetting(row)"
                  icon="el-icon-edit"
                >
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <submission-category-dialog
      :title="
        (categoryDialogRow && categoryDialogRow.informationPackTypeName) || ''
      "
      :visible="categoryDialogVisible"
      :category-options="categoryOptions"
      :submission-configs="getActiveSubmissionConfigs(categoryDialogRow)"
      :initial-data="getCategoryDialogInitialData(categoryDialogRow)"
      @close="handleCloseCategoryDialog"
      @confirm="handleConfirmCategoryDialog"
    />
    <PermissionSettings
      :visible="permissionDialogVisible"
      @close="handleClosePermissionDialog"
    />
    <ContractConfirmConfigDialog
      :visible.sync="configDialogVisible"
      :config-data="contractConfirmConfigData"
      :org-no="currentUser ? currentUser.orgNo : ''"
      :org-id="currentUser ? currentUser.orgId : ''"
      @close="handleCloseConfigDialog"
      @confirm="handleConfirmConfig"
    />
  </div>
</template>

<script>
import {
  queryInformationPackList,
  saveOrUpdateInformationPackList,
} from "@/api/reviewOrder/index.js";
import { reqGetCurrentUser, getDict } from "@oppein/op-utils";
import { getOauth2AccessToken } from "@/api/factory.js";
import SubmissionCategoryDialog from "./SubmissionCategoryDialog.vue";
import PermissionSettings from "./permissionSettings.vue";
import ContractConfirmConfigDialog from "./ContractConfirmConfigDialog.vue";
import { findAuditConfigDetailListByOrgNo } from "@/api/quotaion/designQuotationEContractAuditController/findAuditConfigDetailListByOrgNo/post.js";
import {
  submissionMethodsConfig,
  getAvailableSubmissionMethods,
  getConfigByValue,
} from "./submissionMethodsConfig.js";

const TOKEN_KEY = "Oauth2-AccessToken";

export default {
  name: "CustomerDataPackageSetting",
  components: {
    SubmissionCategoryDialog,
    PermissionSettings,
    ContractConfirmConfigDialog,
  },
  data() {
    return {
      isLoading: false, // 控制加载状态
      // 客户资料包
      customerDataPackage: {},
      categoryDialogVisible: false,
      permissionDialogVisible: false,
      configDialogVisible: false,
      configDialogRow: null,
      contractConfirmConfigData: [],
      categoryDialogRow: null,
      categoryOptions: [],
      currentUser: null, // 当前用户信息
      // 提交方式配置 - 从共享配置文件导入
      // 扩展新的提交方式请修改 submissionMethodsConfig.js 文件
      submissionMethodsConfig,
      // 行操作按钮配置
      rowActionsConfig: [
        {
          key: "permissionSetting",
          label: "权限设置",
          type: "text",
          size: "mini",
          visible: (row) => {
            // 暂时隐藏，根据需要开启
            return (
              false &&
              row.informationPackTypeCode === "customerContract" &&
              row.submissionMethodList &&
              row.submissionMethodList.includes("onlineProcessing")
            );
          },
          handler: "openOnlineSigningDialog",
        },
        {
          key: "categoryMore",
          label: "更多",
          type: "text",
          size: "mini",
          visible: (row) => {
            return (
              row.submissionDimension === 1 &&
              row.submissionMethodList &&
              row.submissionMethodList.length > 1
            );
          },
          handler: "openSubmissionCategoryDialog",
        },
        {
          key: "cofigBtn",
          label: "配置",
          type: "text",
          size: "mini",
          visible: (row) => {
            return row.informationPackTypeName === "客户合同";
          },
          handler: "openConfigBtnDialog",
        },
      ],
      getAvailableSubmissionMethods,
    };
  },
  computed: {
    // 用户选择的iframe URL
    userSelectedIframeUrl() {
      const accessToken = getOauth2AccessToken();
      return `${process.env.DESIGN_QUOTATION_PATH}/activity/userSelection?${TOKEN_KEY}=${accessToken}`;
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      await this.getCategoryOptions();
      this.currentUser = await reqGetCurrentUser();
      this.getCustomerDataPackage();
    },
    /**
     * 初始化数据项中的所有品类列表字段
     * @param {Object} item 数据项
     * @returns {Object} 初始化后的数据项
     */
    initializeCategoryListFields(item) {
      const normalized = { ...item };
      this.submissionMethodsConfig.forEach((config) => {
        if (config.categoryListKey) {
          normalized[config.categoryListKey] = Array.isArray(
            item[config.categoryListKey],
          )
            ? item[config.categoryListKey]
            : [];
        }
      });
      return normalized;
    },
    /**
     * 从数据项中提取所有已配置的品类列表
     * @param {Object} item 数据项
     * @returns {Array<string>} 所有品类的数组（可能包含重复）
     */
    extractAllCategoryLists(item) {
      const allCategoryLists = [];
      this.submissionMethodsConfig.forEach((config) => {
        if (config.categoryListKey) {
          const categoryList = item[config.categoryListKey] || [];
          allCategoryLists.push(...categoryList);
        }
      });
      return allCategoryLists;
    },
    /**
     * 获取数据项中的唯一品类列表
     * @param {Object} item 数据项
     * @returns {Array<string>} 去重后的品类数组
     */
    getUniqueCategoryList(item) {
      const allCategoryLists = this.extractAllCategoryLists(item);
      return [...new Set(allCategoryLists)];
    },
    /**
     * 清空数据项中的所有品类列表字段
     * @param {Object} item 数据项
     * @returns {Object} 清空后的数据项
     */
    clearAllCategoryListFields(item) {
      const cleared = { ...item };
      this.submissionMethodsConfig.forEach((config) => {
        if (config.categoryListKey) {
          cleared[config.categoryListKey] = [];
        }
      });
      return cleared;
    },
    /**
     * 根据提交方式 value 查找第一个匹配的方式
     * @param {Array<string>} submissionMethodList 提交方式列表
     * @returns {string|undefined} 第一个匹配的提交方式 value
     */
    findFirstMatchingMethod(submissionMethodList) {
      if (!Array.isArray(submissionMethodList)) return undefined;

      const allMethodValues = this.submissionMethodsConfig
        .map((config) => config.value)
        .filter(Boolean);
      return submissionMethodList.find((method) =>
        allMethodValues.includes(method),
      );
    },
    /**
     * 获取活跃的提交配置（用于对话框）
     */
    getActiveSubmissionConfigs(row) {
      if (!row) return [];
      return getAvailableSubmissionMethods(row).map((config) => ({
        value: config.value,
        label: config.dialogLabel || config.label,
        categoryListKey: config.categoryListKey,
      }));
    },
    /**
     * 获取对话框初始数据（用于对话框）
     */
    getCategoryDialogInitialData(row) {
      if (!row) return {};
      // 使用本地方法提取品类列表数据
      return this.initializeCategoryListFields(row);
    },
    /**
     * 获取行操作按钮列表
     */
    getRowActions(row) {
      return this.rowActionsConfig.filter((action) => {
        if (typeof action.visible === "function") {
          return action.visible(row);
        }
        return true;
      });
    },
    /**
     * 处理行操作按钮点击
     */
    handleRowAction(action, row) {
      const handler = action.handler;
      if (typeof this[handler] === "function") {
        this[handler](row);
      } else {
        console.warn(`Handler method "${handler}" not found`);
      }
    },
    /**
     * 处理必填校验变化
     * 当必填校验勾选时，自动勾选前台是否展示
     */
    handleRequiredVerificationChange(row) {
      if (row.requiredVerification) {
        row.frontDisplay = true;
      }
    },
    /**
     * 获取品类选项
     */
    async getCategoryOptions() {
      try {
        const list = await getDict("ROOT_category");
        this.categoryOptions = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error(e);
      }
    },
    /**
     * 保存数据 - 供父组件调用
     */
    async saveData() {
      try {
        this.isLoading = true;
        const currentUser = await reqGetCurrentUser();
        const res = await saveOrUpdateInformationPackList({
          ...this.customerDataPackage,
          deparment: currentUser.orgNo, // 设置所属商场
        });
        if (!res.status) {
          throw new Error(res.message || "保存失败");
        }
        return res;
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * 获取客户资料包数据
     */
    async getCustomerDataPackage() {
      try {
        this.isLoading = true;
        const currentUser = await reqGetCurrentUser();
        const response = await queryInformationPackList({
          orgNo: currentUser.orgNo,
        });
        const resp = response.data || {};
        const conditionList = Array.isArray(resp.conditionList)
          ? resp.conditionList.map((item) => {
              // 使用本地方法初始化品类列表字段
              const normalizedItem = this.initializeCategoryListFields({
                submissionDimension:
                  typeof item.submissionDimension === "number"
                    ? item.submissionDimension
                    : 0,
                ...item,
              });
              return normalizedItem;
            })
          : [];
        this.customerDataPackage = {
          ...resp,
          conditionList,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * 刷新数据
     */
    refreshData() {
      this.getCustomerDataPackage();
    },
    /**
     * 判断是否显示在线办理选项
     */
    showOnlineProcessing(row) {
      // 客户合同报价单列表可以在线办理
      if (row.informationPackTypeCode === "customerContractQuotationList") {
        return true;
      }
      const { orgNo } = this.currentUser || {};
      // 客户合同类型在特定组织下可以在线办理
      const allowedOrgList = ["S003116", "S013412", "S000044"];
      if (
        row.informationPackTypeCode === "customerContract" &&
        allowedOrgList.includes(orgNo)
      ) {
        return true;
      }
      return false;
    },
    /**
     * 获取特例名单文本
     */
    getSpecialCaseListText(specialCaseList) {
      if (!Array.isArray(specialCaseList) || specialCaseList.length === 0) {
        return "";
      }
      return specialCaseList.map((item) => item.name).join(", ");
    },
    /**
     * 处理特例名单设置
     */
    handleSpecialCaseSetting(row) {
      const iframe = document.createElement("iframe");
      iframe.src = this.userSelectedIframeUrl;
      iframe.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
        z-index: 9999;
      `;

      // 监听消息
      const handleMessage = (event) => {
        const { type, data } = event.data || {};

        if (type === "USER_SELECTION_READY") {
          iframe.contentWindow.postMessage(
            {
              type: "OPEN_USER_SELECTION",
              params: {
                selectedUser: (row.specialCaseList || []).map((item) => ({
                  userId: item.id,
                  userName: item.name,
                })),
                orgSelectable: false, // 只允许选择人员，不允许选择组织
              },
            },
            "*",
          );
        } else if (type === "USER_SELECTED") {
          if (data) {
            row.specialCaseList = (data.selectedUser || []).map((item) => ({
              id: item.userId,
              name: item.userName,
            }));
          }
          // 关闭 iframe
          document.body.removeChild(iframe);
          window.removeEventListener("message", handleMessage);
        }
      };

      window.addEventListener("message", handleMessage);

      // 添加到页面
      document.body.appendChild(iframe);
    },
    openSubmissionCategoryDialog(row) {
      this.categoryDialogRow = row;
      this.categoryDialogVisible = true;
    },
    openOnlineSigningDialog(row) {
      // this.categoryDialogRow = row;
      this.permissionDialogVisible = true;
      console.log(row, "row");
    },
    async openConfigBtnDialog(row) {
      this.configDialogRow = row;
      // 从接口加载配置数据
      try {
        const { orgNo } = this.currentUser || {};
        if (orgNo) {
          const res = await findAuditConfigDetailListByOrgNo({ orgNo });
          if (res && res.status && res.data) {
            // 转换API返回的数据格式为组件需要的格式
            this.contractConfirmConfigData = this.transformApiDataToConfig(
              res.data,
            );
          } else {
            this.contractConfirmConfigData = row.contractConfirmConfig || [];
          }
        } else {
          this.contractConfirmConfigData = row.contractConfirmConfig || [];
        }
      } catch (error) {
        console.error("加载审批配置失败:", error);
        this.contractConfirmConfigData = row.contractConfirmConfig || [];
      }
      this.configDialogVisible = true;
    },
    /**
     * 将API返回的数据转换为组件需要的格式
     */
    transformApiDataToConfig(apiData) {
      if (!Array.isArray(apiData)) return [];
      return apiData.map((item) => {
        // 转换审核人配置，补齐所有4级
        const apiReviewers = (item.approverRoleConfigList || []).map(
          (role) => ({
            id: (role.roleConfig && role.roleConfig.id) || "",
            level: (role.roleConfig && role.roleConfig.level) || "",
            checked: true,
            alias:
              (role.roleConfig && role.roleConfig.nickName) || role.name || "",
            targetType:
              role.roleConfig && role.roleConfig.specifyType === "SPECIFIC"
                ? "designated"
                : "any",
            designatedUsers: (role.approverList || []).map((a) => ({
              id: a.id,
              name: a.approver,
              account: a.approverAccount,
              approverId: a.approverId,
            })),
          }),
        );
        // 以level为key建立映射，补齐未配置的级别为空
        const reviewerMap = {};
        apiReviewers.forEach((r) => {
          reviewerMap[r.level] = r;
        });
        const reviewers = [1, 2, 3, 4, 5].map((level) => {
          if (reviewerMap[level]) {
            return reviewerMap[level];
          }
          return {
            id: "",
            level,
            checked: false,
            alias: "",
            targetType: "any",
            designatedUsers: [],
          };
        });

        return {
          id: item.id,
          typeCode: item.contractConfirmType,
          typeName: this.getTypeName(item.contractConfirmType),
          templateName: item.contractConfirmTemplateName,
          templateType: item.contractConfirmType,
          enabled: item.enable === 1,
          whetherInitiateAudit: item.whetherInitiateAudit,
          auditMethod: item.auditMethod,
          feishuApprovalFlowId: item.processId,
          canDeduct: item.whetherAssociatedDeduction === 1,
          reviewers,
        };
      });
    },
    /**
     * 根据类型代码获取类型名称
     */
    getTypeName(typeCode) {
      const nameMap = {
        CUSTOMER_CONFIRM: "定制合同确认",
        LAZY_CAT_CUSTOMER_CONFIRM: "懒猫合同确认",
        DECORATION_CUSTOMER_CONFIRM: "装修合同确认",
        STATE_SUBSIDIES_CUSTOMER_CONFIRM: "国补合同确认",
      };
      return nameMap[typeCode] || typeCode;
    },
    handleCloseConfigDialog() {
      this.configDialogVisible = false;
      this.configDialogRow = null;
    },
    handleConfirmConfig(configList) {
      console.log(configList, this.configDialogRow, "configList");
      if (this.configDialogRow) {
        this.$set(this.configDialogRow, "contractConfirmConfig", configList);
      }
      this.handleCloseConfigDialog();
    },
    handleClosePermissionDialog() {
      this.permissionDialogVisible = false;
    },
    handleCloseCategoryDialog() {
      this.categoryDialogVisible = false;
      this.categoryDialogRow = null;
    },
    handleConfirmCategoryDialog(payload) {
      if (this.categoryDialogRow && payload) {
        // 根据配置动态设置所有品类列表
        this.submissionMethodsConfig.forEach((config) => {
          if (
            config.categoryListKey &&
            payload[config.categoryListKey] !== undefined
          ) {
            this.$set(
              this.categoryDialogRow,
              config.categoryListKey,
              payload[config.categoryListKey] || [],
            );
          }
        });
      }
      this.handleCloseCategoryDialog();
    },
    /**
     * 验证并设置客户资料包默认值 - 供父组件调用
     */
    validateAndSetDefaults() {
      const pkg = this.customerDataPackage || {};
      const { conditionList } = pkg;
      // 否的时候，不需要检验
      if (pkg.status) return true;
      if (!Array.isArray(conditionList)) {
        this.$message.error(`资料包配置数据异常,conditionList不能为空`);
        return false;
      }

      const allCategories = this.categoryOptions || [];
      const allCategoryValues = allCategories.map((item) => item.value);

      // 自动修正：必填校验勾选时，确保前台展示也勾选
      conditionList.forEach((row) => {
        if (row.requiredVerification && !row.frontDisplay) {
          row.frontDisplay = true;
        }
      });

      // 校验
      const status = conditionList.every((item) => {
        // 前台展示但未选择提交方式
        if (
          item.frontDisplay &&
          (!item.submissionMethodList || item.submissionMethodList.length === 0)
        ) {
          this.$message.error(
            `${item.informationPackTypeName}提交方式为空，请选择提交方式`,
          );
          return false;
        }

        // 使用本地方法获取所有配置的品类列表
        const allCategoryLists = this.extractAllCategoryLists(item);

        // 允许为空，这种情况会默认全部品类
        if (allCategoryLists.length === 0) {
          return true;
        }

        // 使用本地方法获取唯一品类列表
        // 只有一种提交方式的不需要校验，默认补充全部品类
        const uniqueCategoryList = this.getUniqueCategoryList(item);
        if (
          (allCategoryValues.length !== 0 &&
            allCategoryValues.length === uniqueCategoryList.length) ||
          row.submissionMethodList.length === 1
        ) {
          return true;
        }
        this.$message.error(
          `${item.informationPackTypeName}有品类对应的提交方式未选择`,
        );
        return false;
      });

      if (!status) return false;

      // 维度为按品类，有提交方式，但没有设置过品类，设置全品类默认值
      conditionList.forEach((row) => {
        if (
          row.submissionDimension === 1 &&
          row.submissionMethodList.length >= 0
        ) {
          // 使用本地方法查找第一个匹配的提交方式
          const findKey = this.findFirstMatchingMethod(
            row.submissionMethodList,
          );

          if (!findKey) {
            // 使用本地方法置空所有品类列表
            const cleared = this.clearAllCategoryListFields(row);
            Object.keys(cleared).forEach((key) => {
              this.$set(row, key, cleared[key]);
            });
            return;
          }

          // 使用共享方法根据 value 获取配置
          const methodConfig = getConfigByValue(findKey);
          if (methodConfig && methodConfig.categoryListKey) {
            this.$set(row, methodConfig.categoryListKey, [
              ...allCategoryValues,
            ]);
          }
        }
      });
      return true;
    },
  },
};
</script>

<style lang="scss">
.customer-data-package-setting {
  .customer-data-package-table {
    .el-table th {
      background-color: #dceaff;
      color: #3f3f3f;
    }
  }
}
</style>

<style scoped lang="scss">
.customer-data-package-setting {
  > .title {
    color: var(--color-primary);
    font-size: 16px;
    font-weight: bold;
    border-left: 4px solid var(--color-primary);
    padding-left: 5px;
  }
  > .setting-item {
    background-color: #f1f1f1;
    padding: 10px;
    line-height: 2.5em;
    margin: 10px 0;
    .apply-scope-setting {
      margin-left: 10px;
    }
    .customer-data-package-table {
      margin-top: 10px;
      .special-case-list-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .special-case-text {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-right: 8px;
          text-align: left;
        }
      }
      .submission-methods-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .el-checkbox-group {
          display: flex;
          align-items: center;
        }
        .el-checkbox {
          margin-bottom: 0;
        }
        .el-button {
          margin-left: 10px;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// customerDataPackageSetting.vue?08f42866